// 詳細ペイン「本文」タブ。書類から取り出した本文をページ送りで表示する
import { computed, ref, watch } from 'vue';

export const useBodyText = ({ selectedItem, detailSubTab, applyPageNumber }) => {
  const bodyTextUrlTemplate = ref('');
  const bodyTextItems = ref([]);
  const bodyTextFrom = ref(1);
  const bodyTextTo = ref(null);
  const bodyTextTotalPages = ref(0);
  const bodyTextHasNext = ref(false);
  const bodyTextLoading = ref(false);

  // 1 画面の切れ目は中身の文字数で変わるため、戻るときに逆算できない。
  // 訪れた開始ページを積んでおき、「前へ」はそれを 1 つ戻すだけにする
  const visitedFrom = ref([]);
  // 取得済みの画面。キーは `${videoId}:${from}`
  const cache = ref({});
  let requestSeq = 0;

  const bodyTextHasPrev = computed(() => visitedFrom.value.length > 1);

  const resetBodyText = () => {
    bodyTextItems.value = [];
    bodyTextFrom.value = 1;
    bodyTextTo.value = null;
    bodyTextTotalPages.value = 0;
    bodyTextHasNext.value = false;
    bodyTextLoading.value = false;
    visitedFrom.value = [];
    cache.value = {};
  };

  const applyResponse = (data) => {
    bodyTextItems.value = Array.isArray(data?.items) ? data.items : [];
    bodyTextFrom.value = Number(data?.from) || 1;
    bodyTextTo.value = data?.to ?? null;
    bodyTextTotalPages.value = Number(data?.total_pages) || 0;
    bodyTextHasNext.value = data?.has_next === true;
  };

  // `params` は { from } か { page } のどちらか一方。取得できた画面は
  // 応答の `from` をキーにして同じキャッシュへ入れる (from 経路 / page 経路で共通)。
  // 戻り値は反映できた応答データ。待っている間に捨てられた/失敗した場合は undefined
  const fetchBodyText = async (params) => {
    const item = selectedItem.value;
    const template = bodyTextUrlTemplate.value;
    if (!item || !template || !template.includes('__VIDEO_ID__')) {
      return;
    }
    const videoId = String(item.id);
    if (Object.prototype.hasOwnProperty.call(params, 'from')) {
      const cacheKey = `${videoId}:${params.from}`;
      if (cache.value[cacheKey]) {
        applyResponse(cache.value[cacheKey]);
        return cache.value[cacheKey];
      }
    }

    const seq = ++requestSeq;
    bodyTextLoading.value = true;
    try {
      const url = template.replace('__VIDEO_ID__', encodeURIComponent(videoId));
      const response = await window.axios.get(url, { params });
      // 待っている間に別のファイルへ切り替わっていたら捨てる
      if (seq !== requestSeq || String(selectedItem.value?.id) !== videoId) {
        return;
      }
      const data = response?.data ?? null;
      const cacheKey = `${videoId}:${Number(data?.from) || 1}`;
      cache.value = { ...cache.value, [cacheKey]: data };
      applyResponse(data);
      return data;
    } catch (error) {
      if (seq === requestSeq) {
        bodyTextItems.value = [];
      }
    } finally {
      if (seq === requestSeq) {
        bodyTextLoading.value = false;
      }
    }
  };

  const openBodyText = () => {
    if (visitedFrom.value.length > 0) {
      return;
    }
    visitedFrom.value = [1];
    return fetchBodyText({ from: 1 });
  };

  const goToBodyTextNext = () => {
    if (!bodyTextHasNext.value || bodyTextTo.value === null) {
      return;
    }
    const next = Number(bodyTextTo.value) + 1;
    visitedFrom.value = [...visitedFrom.value, next];
    void fetchBodyText({ from: next });
  };

  const goToBodyTextPrev = () => {
    if (visitedFrom.value.length < 2) {
      return;
    }
    const stack = visitedFrom.value.slice(0, -1);
    visitedFrom.value = stack;
    void fetchBodyText({ from: stack[stack.length - 1] });
  };

  // 一致情報の「本文」行から呼ばれる。指定ページを含む画面を表示する
  const ensureBodyPageLoaded = (pageNo) => {
    const page = Number(pageNo);
    if (!Number.isInteger(page) || page < 1) {
      return;
    }
    // 表示中の画面に既にそのページがあれば、通信も状態変更もせず終わる
    // (一致情報の同じ行を続けて押しても取得が起きないように)
    if (bodyTextItems.value.some((it) => Number(it?.page_no) === page)) {
      return;
    }
    // 既に取得済みの画面 (from〜to にそのページを含むもの) があればそれを使い、
    // サーバへは行かない。ただし window_starts を持たない応答は、ページ送りで
    // 取ったものでそこへ至る画面の並びが分からないため使えない
    const videoId = String(selectedItem.value?.id);
    const cachedKey = Object.keys(cache.value).find((key) => {
      if (!key.startsWith(`${videoId}:`)) {
        return false;
      }
      const cached = cache.value[key];
      return (
        Array.isArray(cached?.window_starts) &&
        page >= Number(cached?.from) &&
        page <= Number(cached?.to)
      );
    });
    if (cachedKey) {
      const cached = cache.value[cachedKey];
      applyResponse(cached);
      visitedFrom.value = cached.window_starts;
      return;
    }

    // await に入る前に同期で仮の履歴を積んでおく。
    // watch(detailSubTab) の openBodyText() は「履歴が空なら from=1 を取りに行く」実装のため、
    // ここで積まずに await へ入ると detailSubTab の代入で openBodyText() が同時に走り、
    // 無駄な from=1 の取得と競合してしまう
    visitedFrom.value = [page];
    return fetchBodyText({ page }).then((data) => {
      if (!data) {
        return;
      }
      const starts = Array.isArray(data.window_starts) ? data.window_starts : null;
      visitedFrom.value = starts && starts.length > 0 ? starts : [Number(data.from) || page];
    });
  };

  const jumpToBodyPage = (item) => {
    applyPageNumber?.({ page_no: item?.page_no });
  };

  const initBodyText = (dataSource) => {
    bodyTextUrlTemplate.value =
      dataSource?.getAttribute('data-video-body-text-url-template') || '';
  };

  watch(selectedItem, (next) => {
    resetBodyText();
    // 本文を持たないファイルに切り替わったのにタブだけ残ると、ボタンが消えて中身も空になる
    if (detailSubTab.value === 'body' && !next?.has_body_text) {
      detailSubTab.value = 'info';
    }
  });

  // タブを開いたときに最初の 1 画面を取りに行く
  watch(detailSubTab, (next) => {
    if (next === 'body') {
      openBodyText();
    }
  });

  return {
    bodyTextItems,
    bodyTextFrom,
    bodyTextTo,
    bodyTextTotalPages,
    bodyTextLoading,
    bodyTextHasPrev,
    bodyTextHasNext,
    goToBodyTextPrev,
    goToBodyTextNext,
    jumpToBodyPage,
    initBodyText,
    ensureBodyPageLoaded,
  };
};
